// frontend/src/hooks/useEntropyNFT.js
import { useState, useEffect, useCallback } from "react";
import web3Service from "../services/web3Service";

/**
 * Hook React pour interagir avec le contrat EntropyNFT
 */
export function useEntropyNFT() {
  const [contract, setContract] = useState(null);
  const [address, setAddress] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialise le contrat
  useEffect(() => {
    initContract();
  }, []);

  const initContract = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Si le contrat n'existe pas, on log silencieusement ou on ignore
      try {
        const entropyNFT = await web3Service.getContract("EntropyNFT");
        setContract(entropyNFT);

        const addr = await entropyNFT.getAddress();
        setAddress(addr);

        console.log("✅ EntropyNFT initialisé à", addr);
      } catch (innerErr) {
        console.warn("⚠️ EntropyNFT non disponible (optionnel). Widget masqué.");
        // Pas de setError pour ne pas afficher d'erreur rouge
      }

    } catch (err) {
      // Catch global au cas où
      console.warn("⚠️ Problème global EntropyNFT:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Récupère le nom de la collection
  const getName = useCallback(async () => {
    if (!contract) return "";

    try {
      return await contract.name();
    } catch (err) {
      console.error("❌ Erreur getName:", err);
      throw err;
    }
  }, [contract]);

  // Récupère le symbole
  const getSymbol = useCallback(async () => {
    if (!contract) return "";

    try {
      return await contract.symbol();
    } catch (err) {
      console.error("❌ Erreur getSymbol:", err);
      throw err;
    }
  }, [contract]);

  // Récupère le total supply
  const getTotalSupply = useCallback(async () => {
    if (!contract) return 0;

    try {
      const supply = await contract.totalSupply();
      return Number(supply);
    } catch (err) {
      console.error("❌ Erreur getTotalSupply:", err);
      throw err;
    }
  }, [contract]);

  // Récupère le solde d'une adresse (nombre de NFTs)
  const getBalance = useCallback(async (userAddress) => {
    if (!contract) return 0;

    try {
      const balance = await contract.balanceOf(userAddress);
      return Number(balance);
    } catch (err) {
      console.error("❌ Erreur getBalance:", err);
      throw err;
    }
  }, [contract]);

  // Récupère le propriétaire d'un NFT
  const getOwnerOf = useCallback(async (tokenId) => {
    if (!contract) return "";

    try {
      return await contract.ownerOf(tokenId);
    } catch (err) {
      console.error("❌ Erreur getOwnerOf:", err);
      throw err;
    }
  }, [contract]);

  // Récupère l'URI d'un token
  const getTokenURI = useCallback(async (tokenId) => {
    if (!contract) return "";

    try {
      return await contract.tokenURI(tokenId);
    } catch (err) {
      console.error("❌ Erreur getTokenURI:", err);
      throw err;
    }
  }, [contract]);

  // Mint un nouveau NFT
  const mint = useCallback(async (toAddress, tokenURI) => {
    if (!contract) throw new Error("Contrat non initialisé");

    try {
      const tx = await contract.mint(toAddress, tokenURI);
      console.log("📤 Transaction envoyée:", tx.hash);
      
      const receipt = await tx.wait();
      console.log("✅ Transaction confirmée:", receipt.transactionHash);
      
      return receipt;
    } catch (err) {
      console.error("❌ Erreur mint:", err);
      throw err;
    }
  }, [contract]);

  // Transfère un NFT
  const transfer = useCallback(async (fromAddress, toAddress, tokenId) => {
    if (!contract) throw new Error("Contrat non initialisé");

    try {
      const tx = await contract.transferFrom(fromAddress, toAddress, tokenId);
      console.log("📤 Transaction envoyée:", tx.hash);
      
      const receipt = await tx.wait();
      console.log("✅ Transaction confirmée:", receipt.transactionHash);
      
      return receipt;
    } catch (err) {
      console.error("❌ Erreur transfer:", err);
      throw err;
    }
  }, [contract]);

  // Approuve un transfert
  const approve = useCallback(async (toAddress, tokenId) => {
    if (!contract) throw new Error("Contrat non initialisé");

    try {
      const tx = await contract.approve(toAddress, tokenId);
      console.log("📤 Transaction envoyée:", tx.hash);
      
      const receipt = await tx.wait();
      console.log("✅ Transaction confirmée:", receipt.transactionHash);
      
      return receipt;
    } catch (err) {
      console.error("❌ Erreur approve:", err);
      throw err;
    }
  }, [contract]);

  // Récupère l'adresse approuvée pour un token
  const getApproved = useCallback(async (tokenId) => {
    if (!contract) return "";

    try {
      return await contract.getApproved(tokenId);
    } catch (err) {
      console.error("❌ Erreur getApproved:", err);
      throw err;
    }
  }, [contract]);

  // Écoute les événements Transfer
  const onTransfer = useCallback((callback) => {
    if (!contract) return;

    const filter = contract.filters.Transfer();
    
    contract.on(filter, (from, to, tokenId, event) => {
      callback({
        from,
        to,
        tokenId: Number(tokenId),
        transactionHash: event?.log?.transactionHash || event?.transactionHash || "",
        blockNumber: event?.log?.blockNumber || event?.blockNumber || 0
      });
    });

    // Retourne une fonction de nettoyage
    return () => {
      contract.off(filter);
    };
  }, [contract]);

  return {
    contract,
    address,
    isLoading,
    error,
    getName,
    getSymbol,
    getTotalSupply,
    getBalance,
    getOwnerOf,
    getTokenURI,
    mint,
    transfer,
    approve,
    getApproved,
    onTransfer,
  };
}
